import { useContext, useEffect, useRef } from "react";

import { useTranslation } from "react-i18next";

import AlertContext, { ALERT_STATUS } from "context/alert/alertContext";
import { useSignalRForBgActions } from "context/signalR/hooks/useSignalRForBgActions";
import { DEVICE_EXPORT } from "shared/constants/strings";
import { downloadFile } from "shared/functions";
import { useApi } from "shared/hooks/useApi";
import { ENDPOINTS } from "shared/types/enums";

import { BACKROUND_ACTION_TITLES } from "../constants";
import { renderTranslatedDescription } from "../functions";
import { BACKGROUND_ACTION_STATUS } from "../types";

const useBackgroundActions = () => {
    const {
        backgroundActionId,
        backgroundActionSuccessCallback,
        backgroundActionFailureCallback,
        setAlert
    } = useContext(AlertContext);

    const { t } = useTranslation();
    const { isCanceled, getData, handleResponse, updateData } = useApi();

    useSignalRForBgActions(backgroundActionId, data =>
        getBackgroundActionStatus(data.resourceId, null, null)
    );

    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const notifyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // Prevent from triggering the effect if there is no background action
        if (!backgroundActionId) return;

        const notifyMeLater = async () => {
            backgroundActionSuccessCallback?.();

            try {
                await updateData(
                    ENDPOINTS.BackgroundActions,
                    backgroundActionId,
                    { notify: true }
                );
            } catch (error) {
                if (!isCanceled(error)) {
                    handleResponse(error);
                }
            }
        };

        // "Notify me later" logic
        notifyTimeoutRef.current = setTimeout(() => {
            setAlert({
                status: ALERT_STATUS.Info,
                title: t("Flash##notify when done title"),
                secondaryBtn: {
                    label: t("Button##notify when done"),
                    action: () => {
                        void notifyMeLater();
                    }
                },
                hasCloseBtn: false,
                hasAutoHide: false
            });
        }, 15000);

        // Initial status check with retry logic
        getBackgroundActionStatus(backgroundActionId, 1000, 2000);

        // eslint-disable-next-line
    }, [backgroundActionId]);

    const getBackgroundActionStatus = async (
        id: string,
        delay: number | null,
        increment: number | null
    ) => {
        // Prevent from calling the API on recurring events if there is no background action
        if (!backgroundActionId) return;

        try {
            const { data } = await getData(
                `${ENDPOINTS.BackgroundActions}/${id}`
            );

            const { notify, status: newStatus, type, result_file_id } = data;

            if (notify) {
                clearTimeoutRefs();
            } else {
                handleBackgroundActionStatus(
                    newStatus,
                    id,
                    data,
                    delay,
                    increment,
                    type,
                    result_file_id
                );
            }
        } catch (error) {
            if (!isCanceled(error)) {
                backgroundActionFailureCallback?.();
                clearTimeoutRefs();
                handleResponse(error);
            }
        }
    };

    const handleBackgroundActionStatus = (
        status: BACKGROUND_ACTION_STATUS,
        id: string,
        data: any,
        delay: number | null,
        increment: number | null,
        type: string,
        fileId: number | null
    ) => {
        const details = data.structured_details || data.details;

        switch (status) {
            case BACKGROUND_ACTION_STATUS.Completed: {
                clearTimeoutRefs();

                const titleKey =
                    BACKROUND_ACTION_TITLES[
                        data.type as keyof typeof BACKROUND_ACTION_TITLES
                    ]?.success;

                const isDeviceExport = type === DEVICE_EXPORT;

                setAlert({
                    status: ALERT_STATUS.Success,
                    title: t(`Notifications##title##${titleKey}`),
                    description: renderTranslatedDescription(details, t),
                    hasAutoHide: !isDeviceExport,
                    ...(isDeviceExport && fileId !== null
                        ? {
                              secondaryBtn: {
                                  label: t("General##download file"),
                                  action: () =>
                                      downloadFile({
                                          id: fileId,
                                          getData,
                                          handleResponse
                                      })
                              }
                          }
                        : {})
                });

                // Close dialog and refresh data
                backgroundActionSuccessCallback?.();

                break;
            }
            case BACKGROUND_ACTION_STATUS.Failed: {
                clearTimeoutRefs();

                const titleKey =
                    BACKROUND_ACTION_TITLES[
                        data.type as keyof typeof BACKROUND_ACTION_TITLES
                    ]?.failed;

                setAlert({
                    status: ALERT_STATUS.Critical,
                    title: t(`Notifications##title##${titleKey}`),
                    description: renderTranslatedDescription(details, t),
                    hasCloseBtn: false,
                    hasAutoHide: false
                });

                // Remove the loading
                backgroundActionFailureCallback?.();

                break;
            }
            default: {
                const hasRetry = delay !== null && increment !== null;

                if (hasRetry) {
                    retryTimeoutRef.current = setTimeout(() => {
                        const MAX_DELAY = 9000;
                        const newDelay = delay + increment;

                        const newFinalDelay =
                            newDelay > MAX_DELAY ? MAX_DELAY : newDelay;

                        getBackgroundActionStatus(id, newFinalDelay, increment);
                    }, delay);
                }
            }
        }
    };

    const clearTimeoutRefs = () => {
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
        }

        if (notifyTimeoutRef.current) {
            clearTimeout(notifyTimeoutRef.current);
        }
    };
};

export { useBackgroundActions };
